import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import {
    analyzeBlogQuality,
    extractInternalBlogSlugs,
    type BlogQualityReport,
    type BlogSlugCheck,
} from '../lib/blogQualityChecks';

interface SlugStatusRow {
    slug?: string;
    status?: string;
}

function toStatusMap(data: SlugStatusRow | SlugStatusRow[] | null | undefined): Record<string, string> {
    const rows = normalizeRpcResponse(data);
    const map: Record<string, string> = {};
    for (const row of rows) {
        const slug = String(row?.slug ?? '').trim().toLowerCase();
        if (!slug) continue;
        map[slug] = String(row?.status ?? '');
    }
    return map;
}

export function useBlogSlugStatus(slugs: string[]) {
    const sorted = useMemo(
        () => [...new Set(slugs.map((slug) => slug.trim().toLowerCase()).filter(Boolean))].sort(),
        [slugs],
    );
    return useQuery({
        queryKey: ['admin', 'blog', 'slug-status', sorted.join('\n')],
        enabled: sorted.length > 0,
        staleTime: 30_000,
        retry: false,
        queryFn: async () => {
            const { data, error } = await dbClient.rpc<SlugStatusRow | SlugStatusRow[]>(
                'admin_blog_slugs_status',
                { p_slugs: sorted },
            );
            if (error) throw new Error(error.message);
            return toStatusMap(data);
        },
    });
}

export function useBlogPostQuality(fields: {
    title: string;
    metaTitle: string;
    metaDescription: string;
    excerpt: string;
    bodyHtml: string;
    currentSlug: string;
}): { report: BlogQualityReport; checkingLinks: boolean } {
    const slugs = useMemo(() => extractInternalBlogSlugs(fields.bodyHtml), [fields.bodyHtml]);
    const statusQuery = useBlogSlugStatus(slugs);

    const report = useMemo(() => {
        let slugCheck: BlogSlugCheck;
        if (slugs.length === 0) slugCheck = { state: 'ready', statusBySlug: {} };
        else if (statusQuery.isError) slugCheck = { state: 'error' };
        else if (statusQuery.isSuccess) slugCheck = { state: 'ready', statusBySlug: statusQuery.data ?? {} };
        else slugCheck = { state: 'pending' };
        return analyzeBlogQuality({
            title: fields.title,
            metaTitle: fields.metaTitle,
            metaDescription: fields.metaDescription,
            excerpt: fields.excerpt,
            bodyHtml: fields.bodyHtml,
            currentSlug: fields.currentSlug,
            slugCheck,
        });
    }, [
        fields.title,
        fields.metaTitle,
        fields.metaDescription,
        fields.excerpt,
        fields.bodyHtml,
        fields.currentSlug,
        slugs,
        statusQuery.isError,
        statusQuery.isSuccess,
        statusQuery.data,
    ]);

    return {
        report,
        checkingLinks: slugs.length > 0 && statusQuery.isFetching,
    };
}
